import type { Timestamp } from 'firebase/firestore';

export type Rol = 'admin' | 'doctor' | 'recepcion';

export type Bloque = 'clinica';

// Los petshops ahora se cargan de la base (autoservicio). El id es texto.
export type PetshopId = string;

export interface Petshop {
  id: string;
  nombre: string;
  nombreCorto?: string | null;
  dueno?: string | null;
  telefono?: string | null;
  qrUrl?: string | null;
  orden?: number;
  activo?: boolean;
}

// Fallback de nombres para los iniciales (por si la lista aún no cargó).
export const PETSHOPS: Record<string, { nombre: string; corto: string }> = {
  danna: { nombre: 'Petshop Danna Zapata', corto: 'Danna Zapata' },
  bruno: { nombre: 'Petshop Bruno', corto: 'Bruno' },
};

export type MetodoPago = 'efectivo' | 'qr' | 'transferencia' | 'tarjeta' | 'otro';

export interface PetshopVenta {
  id: string;
  petshop: PetshopId;
  descripcion: string;
  monto: number;
  cantidad?: number | null;
  metodoPago: MetodoPago;
  productoId?: string | null;
  productoCategoria?: string | null;
  fecha: Timestamp;
  autorUid: string;
  autorNombre: string;
  rendicionId: string | null;
  anulada?: boolean;
  anuladaEn?: Timestamp | null;
  anuladaPorNombre?: string | null;
  motivoAnulacion?: string | null;
  createdAt: Timestamp;
}

export interface PetshopProducto {
  id: string;
  petshop: PetshopId;
  nombre: string;
  categoria: string;
  precio: number;
  stock?: number;
  notas?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface PetshopRendicion {
  id: string;
  petshop: PetshopId;
  mesISO: string; // 'YYYY-MM'
  total: number;
  cantidadVentas: number;
  metodoTransferencia: MetodoPago;
  comprobanteUrl: string | null;
  comprobanteNombre: string | null;
  notas: string | null;
  rendidaPorUid: string;
  rendidaPorNombre: string;
  rendidaEn: Timestamp;
}

export type EstadoSolicitud =
  | 'borrador'
  | 'pendiente'
  | 'aprobada'
  | 'comprada'
  | 'entregada'
  | 'entregada_parcial'
  | 'cancelada'
  | 'rechazada';

export type TipoMovimiento = 'ingreso' | 'egreso';

export type SeveridadAlerta = 'info' | 'warning' | 'critical';

export type TipoAlerta = 'stock_bajo' | 'vencimiento_proximo' | 'vencido' | 'agotado';

export interface Usuario {
  uid: string;
  email: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
  telefono?: string;
  fotoUrl?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface Proveedor {
  id: string;
  nombre: string;
  whatsapp: string;
  telefono?: string;
  email?: string;
  notas?: string;
  /** Horario libre (legacy). */
  horarios?: string;
  /** Hora de apertura en formato HH:MM. */
  horaApertura?: string;
  /** Hora de cierre en formato HH:MM. */
  horaCierre?: string;
  /** Días en que no responde. 0 = domingo, 6 = sábado. */
  diasNoAtiende?: number[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ProductoBase {
  id: string;
  nombre: string;
  presentacion: string;
  categoria?: string;
  proveedorId?: string;
  proveedorNombre?: string;
  proveedorWhatsapp?: string;
  vencimiento?: Timestamp | null;
  stock: number;
  stockMin: number;
  stockBajo: boolean;
  ubicacion?: string;
  notas?: string;
  fotoUrl?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ProductoVenta extends ProductoBase {
  precioCompra: number;
  precioVenta?: number;
}

export interface InsumoInterno extends ProductoBase {
  // sin precio de venta - es uso clínico
}

/**
 * Formularios preimpresos en papel: castración, autorización, vacuna rabia, etc.
 * Más simple que un producto — sin proveedor, sin vencimiento, sin precio.
 */
export interface Formulario {
  id: string;
  nombre: string;
  descripcion?: string;
  stock: number;
  stockMin: number;
  stockBajo: boolean;
  ubicacion?: string;
  notas?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface UsoFormulario {
  id: string;
  formularioId: string;
  formularioNombre: string;
  cantidad: number;
  numeroTalonario?: string;
  solicitadoPor?: string;
  motivo?: string;
  fecha: Timestamp;
  autorUid: string;
  autorNombre: string;
}

export interface MovimientoStock {
  id: string;
  tipo: 'entrada' | 'salida' | 'ajuste';
  cantidad: number;
  motivo?: string;
  autorUid: string;
  autorNombre: string;
  fecha: Timestamp;
}

export type EstadoPago = 'pagado' | 'pendiente';

export interface MovimientoContable {
  id: string;
  bloque: Bloque;
  tipo: TipoMovimiento;
  categoria: string;
  monto: number;
  metodoPago: 'efectivo' | 'qr' | 'transferencia' | 'tarjeta' | 'otro';
  descripcion: string;
  beneficiario?: string;
  proveedorId?: string | null;
  proveedorNombre?: string | null;
  numeroFactura?: string | null;
  adjuntoUrl?: string | null;
  /** pagado (default) o pendiente de pago al proveedor. */
  estadoPago?: EstadoPago;
  pagadoEn?: Timestamp | null;
  /** URL al comprobante (recibo, captura transferencia, etc) en Cloudinary. */
  comprobantePagoUrl?: string | null;
  comprobantePagoNombre?: string | null;
  fecha: Timestamp;
  autorUid: string;
  autorNombre: string;
  createdAt: Timestamp;
}

export type TipoSolicitud = 'insumos' | 'farmacia';

export interface ItemSolicitud {
  /** ID del producto en la colección correspondiente (insumos_internos o productos_venta). */
  insumoId: string;
  insumoNombre: string;
  cantidad: number;
  /** Cantidad realmente entregada (por defecto 0; al entregar todo, igual a cantidad). */
  cantidadEntregada?: number;
}

export interface Solicitud {
  id: string;
  /** Tipo de productos solicitados. 'insumos' es legacy default. */
  tipo?: TipoSolicitud;
  area: string; // AreaId — 'consultorio_1', 'quirofano', etc.
  areaNombre: string;
  /** Última persona que la modificó (no es dueña). */
  ultimoEditorUid: string;
  ultimoEditorNombre: string;
  /** Persona que la creó originalmente. Legacy: en solicitudes viejas era el dueño. */
  doctorUid?: string;
  doctorNombre?: string;
  items: ItemSolicitud[];
  estado: EstadoSolicitud;
  nota?: string;
  notaAdmin?: string;
  creadaEn: Timestamp;
  actualizadaEn?: Timestamp;
  enviadaEn?: Timestamp;
  aprobadaEn?: Timestamp;
  compradaEn?: Timestamp;
  entregadaEn?: Timestamp;
  canceladaEn?: Timestamp;
}

export type TipoReporte = 'error' | 'optimizacion';
export type EstadoReporte = 'nuevo' | 'en_proceso' | 'resuelto';

export interface Reporte {
  id: string;
  tipo: TipoReporte;
  titulo: string;
  descripcion: string;
  imagenes: string[];
  estado: EstadoReporte;
  autorUid: string;
  autorNombre: string;
  autorRol?: string;
  creadoEn: Timestamp;
  // Respuesta del admin — visible para quien reportó.
  solucion?: string;
  resueltoPorNombre?: string;
  resueltoEn?: Timestamp;
}

export interface Alerta {
  id: string;
  tipo: TipoAlerta;
  severidad: SeveridadAlerta;
  productoId: string;
  productoNombre: string;
  origen: 'venta' | 'interno';
  proveedorWhatsapp?: string;
  proveedorNombre?: string;
  detalle?: string;
  leida: boolean;
  creadaEn: Timestamp;
}
